import React from "react";
import { Pie } from "@visx/shape";
import { Group } from "@visx/group";
import { Text } from "@visx/text";

import { useTheme } from "..";
import { PieChartProps } from "./PieChart.types";

function PieChart<T>(props: PieChartProps<T>) {
  const {
    data,
    width,
    valueAccessor,
    colorAccessor,
    label = "Total",
    thickness = 10,
    labelFontSize = 12,
    valueFontSize = 20,
    ornamentRadius = width / 2 - thickness - 12,
  } = props;

  const theme = useTheme();

  const [active, setActive] = React.useState<T>();

  const formatValue = (value: number) => Math.round(value).toLocaleString();

  const handleMouseEnter = (item: T) => {
    setActive(item);
  };

  const handleMouseLeave = () => {
    setActive(undefined);
  };

  const half = width / 2;
  const total = data.reduce((acc, x) => acc + valueAccessor?.(x), 0);
  const innerRadius = half - thickness;
  const value = active
    ? formatValue(valueAccessor(active))
    : formatValue(total);

  if (data.length === 0) {
    return (
      <svg width={width} height={width}>
        <Group top={half} left={half}>
          <circle r={half} fill={theme.palette.grey[200]} />
          <circle r={innerRadius} fill={theme.palette.common.white} />
          <circle
            r={ornamentRadius}
            stroke={theme.palette.grey[600]}
            strokeDasharray="1 8"
            fill="transparent"
            strokeWidth={1}
          />
          <Text
            textAnchor="middle"
            fontSize={labelFontSize}
            fontWeight={700}
            fill={theme.palette.grey[700]}
            dy={labelFontSize * -2}
          >
            {label}
          </Text>
          <Text
            textAnchor="middle"
            fontSize={valueFontSize}
            fontWeight={600}
            dy={valueFontSize / 2}
            fill={theme.palette.grey[900]}
          >
            0
          </Text>
        </Group>
      </svg>
    );
  }

  const noZeroData = data.filter((item) => valueAccessor(item) > 0);

  return (
    <svg width={width} height={width}>
      <circle
        cx={half}
        cy={half}
        r={ornamentRadius}
        stroke={theme.palette.grey[600]}
        strokeDasharray="1 8"
        fill="transparent"
        strokeWidth={1}
      />
      <Group top={half} left={half}>
        <Pie
          data={noZeroData}
          pieValue={valueAccessor}
          outerRadius={half}
          innerRadius={innerRadius}
          padAngle={0.04}
          cornerRadius={9999}
        >
          {(pie) => {
            return pie.arcs.map((arc, index) => {
              const color = colorAccessor(arc.data, index);
              const arcPath: any = pie.path(arc);
              return (
                <g
                  key={index}
                  onMouseEnter={() => handleMouseEnter(arc.data)}
                  onMouseLeave={handleMouseLeave}
                >
                  <path d={arcPath} fill={color} />
                </g>
              );
            });
          }}
        </Pie>
        <Text
          textAnchor="middle"
          fontSize={labelFontSize}
          fontWeight={700}
          fill={theme.palette.grey[700]}
          dy={labelFontSize * -2}
        >
          {label}
        </Text>
        <Text
          textAnchor="middle"
          fontSize={valueFontSize}
          fontWeight={600}
          dy={valueFontSize / 2}
          fill={theme.palette.grey[900]}
        >
          {value}
        </Text>
      </Group>
    </svg>
  );
}

export default PieChart;
